import _ from 'lodash';
import { CategoryType, ChangeInterpretation, ShowChangeAs, } from './types/prism';
export const DEFAULT_DECIMAL_PRECISION = 2;
export const CLIENT_STATE_KEYS = {
    roundedNumbers: 'roundedNumbers',
    yAxisShared: 'yAxisShared',
    gridLineConfig: 'gridLineConfig',
    IS_STACKED_AS_PERCENT: 'yAxis.isStackedAsPercent',
    columnProperties: 'columnProperties',
    chartProperties: 'chartProperties',
    axisProperties: 'axisProperties',
    tooltipConfig: 'tooltipConfig',
    dataLabels: 'dataLabels',
    detailedLabels: 'detailedLabels',
    conditionalFormatting: 'conditionalFormatting',
    allLabels: 'allLabels',
    summariesNumberFormatConfig: 'summariesNumberFormatConfig',
    customProps: 'customProps',
    linkedColumns: 'linkedColumns',
    yAxisRange: 'yAxisRange',
    axisType: 'axisType',
    name: 'name',
    format: 'format',
    isOpposite: 'isOpposite',
    dynamicTitle: 'dynamicTitle',
    SYSTEM_SERIES_COLORS: 'systemSeriesColors',
    SERIES_COLORS: 'seriesColors',
    SYSTEM_MULTI_COLOR_SERIES_COLORS: 'systemMultiColorSeriesColors',
    MULTI_COLOR_SERIES_COLORS: 'multiColorSeriesColors',
    pivotState: 'pivotState',
    hidePivotSummaries: 'hidePivotSummaries',
    summaryMode: 'summaryMode',
    summaryFormat: 'summaryFormat',
    useFlatLayout: 'useFlatLayout',
    dataFieldArea: 'dataFieldArea',
    showLegend: 'showLegend',
    legendPosition: 'legendPosition',
    kpiDisplayProperties: 'kpiDisplayProperties',
    kpiColumnProperties: 'kpiColumnProperties',
    isHeatmapOverlayed: 'isHeatmapOverlayed',
    showLinearRegressionLine: 'showLinearRegressionLine',
    markersEnabled: 'markersEnabled',
    visibleSeriesNames: 'visibleSeriesNames',
    axisExtremes: 'axisExtremes',
    gridLines: 'gridLines',
    isZoomed: 'isZoomed',
    mapviewport: 'mapviewport',
    showStackedLabels: 'showStackedLabels',
    showRoundedNumbers: 'showRoundedNumbers',
    isStackedAsPercent: 'isStackedAsPercent',
    stackedAsPercentFormat: 'stackedAsPercentFormat',
    responsiveLayoutDisabled: 'responsiveLayoutDisabled',
    responsiveLayoutPreference: 'responsiveLayoutPreference',
    chartSpecific: 'chartSpecific',
    version: 'version',
    dataSize: 'dataSize',
    customColorSelectorArray: 'customColorSelectorArray',
    answerColumnProperty: 'answerColumnProperty',
    showPivotSummaryPrior: 'showPivotSummaryPrior',
    pivotSummariesState: 'pivotSummariesState',
    handleMissingValues: 'handleMissingValues',
    excludeNullValues: 'excludeNullValues',
};
export var CLIENT_STATE_VERSIONS;
(function (CLIENT_STATE_VERSIONS) {
    CLIENT_STATE_VERSIONS["V2"] = "V2";
    CLIENT_STATE_VERSIONS["V2dot1"] = "V2.1";
    CLIENT_STATE_VERSIONS["V2dot2"] = "V2.2";
    CLIENT_STATE_VERSIONS["V3"] = "V3";
    CLIENT_STATE_VERSIONS["V4"] = "V4";
    CLIENT_STATE_VERSIONS["V4dot1"] = "V4DOT1";
    CLIENT_STATE_VERSIONS["V4dot2"] = "V4DOT2";
})(CLIENT_STATE_VERSIONS || (CLIENT_STATE_VERSIONS = {}));
export const CHART_CLIENT_STATE_LATEST_VERSION = CLIENT_STATE_VERSIONS.V4dot2;
export const DEFAULT_RANGE_PARAMS = {
    __typename: 'RangeParam',
    max: null,
    min: null,
};
export const DEFAULT_GRIDLINES = {
    __typename: 'GridLineProperties',
    xGridlineEnabled: null,
    yGridlineEnabled: null,
};
export const DEFAULT_AXIS_EXTREMES = {
    __typename: 'AxisExtremesProps',
    x: null,
    y: null,
};
export const DEFAULT_PIVOT_STATE = {
    __typename: 'PivotState',
    columnExpandedPaths: [],
    rowExpandedPaths: [],
    fields: [],
};
export const DEFAULT_CHART_COLUMN_PROPERTY_MAP = {
    __typename: 'ChartColumnPropertyMap',
    columnId: null,
    columnProperty: null,
};
export const DEFAULT_CHART_VIZ_PROPERTIES = {
    __typename: 'ChartVizColumnProperties',
    conditionalFormatting: null,
    dataLabels: null,
    handleMissingValues: null,
    excludeNullValues: null,
    customProps: null,
    kpiColumnProperties: null,
};
export const DEFAULT_FORMAT_CONFIG = {
    __typename: 'FormatConfig',
    category: null,
    currencyFormatConfig: null,
    customFormatConfig: null,
    isCategoryEditable: null,
    numberFormatConfig: null,
    percentageFormatConfig: null,
};
export const DEFAULT_CURRENCY_FORMAT_CONFIG = {
    __typename: 'CurrencyFormatConfig',
    decimals: null,
    locale: null,
    removeTrailingZeroes: null,
    toSeparateThousands: null,
    unit: null,
};
export const DEFAULT_PIVOT_SUMMARIES_STATE = {
    __typename: 'PivotSummariesState',
    showRowTotals: null,
    showColumnTotals: null,
    showRowGrandTotals: null,
    showColumnGrandTotals: null,
};
export const DEFAULT_CHART_SPECIFIC = {
    __typename: 'ChartSpecificProperties',
    hidePivotSummaries: null,
    isHeatmapOverlayed: null,
    isStackedAsPercent: null,
    markersEnabled: null,
    pivotState: null,
    stackedAsPercentFormat: null,
    summaryFormat: null,
    summaryMode: null,
    useFlatLayout: null,
    customProps: null,
    dataFieldArea: null,
    showPivotSummaryPrior: null,
    pivotSummariesState: DEFAULT_PIVOT_SUMMARIES_STATE,
};
export const DEFAULT_KPI_DISPLAY_PROPERTIES = {
    __typename: 'KpiDisplayProperties',
    showChange: true,
    showChangeAs: ShowChangeAs.Percent,
    changeInterpretation: ChangeInterpretation.UpwardIsGood,
    kpiDisplayColorArray: [],
};
export const DEFAULT_CHART_PROPERTIES = {
    __typename: 'ChartProperties',
    allLabels: null,
    axisExtremes: null,
    chartSpecific: DEFAULT_CHART_SPECIFIC,
    dataSize: null,
    gridLines: null,
    isZoomed: null,
    mapviewport: null,
    responsiveLayoutDisabled: null,
    responsiveLayoutPreference: null,
    showLinearRegressionLine: null,
    showStackedLabels: null,
    visibleSeriesNames: [],
    showLegend: null,
    legendPosition: null,
    kpiDisplayProperties: DEFAULT_KPI_DISPLAY_PROPERTIES,
};
export const DEFAULT_PERCENTAGE_FORMAT = {
    ...DEFAULT_FORMAT_CONFIG,
    category: CategoryType.Percentage,
    percentageFormatConfig: {
        __typename: 'PercentageFormatConfig',
        decimals: DEFAULT_DECIMAL_PRECISION,
        removeTrailingZeroes: false,
    },
    isCategoryEditable: false,
};
export const DEFAULT_HEADLINE_VIZ_PROPERTY = {
    __typename: 'HeadlineVizProperties',
    conditionalFormatting: null,
    summariesNumberFormatConfig: null,
};
export const DEFAULT_ANSWER_COLUMN_PROPERTY = {
    __typename: 'AnswerColumnProperty',
    format: null,
    sortConfig: null,
};
export const DEFAULT_NUMBER_FORMAT_CONFIG = {
    __typename: 'NumberFormatConfig',
    decimals: null,
    negativeValueFormat: null,
    removeTrailingZeroes: null,
    toSeparateThousands: null,
    unit: null,
};
export const DEFAULT_CUSTOM_FORMAT_CONFIG = {
    __typename: 'CustomFormatConfig',
    format: null,
};
export const DEFAULT_PERCENTAGE_FORMAT_CONFIG = {
    __typename: 'PercentageFormatConfig',
    decimals: null,
    removeTrailingZeroes: null,
};
export const TABLE_CLIENT_STATE_KEYS = {
    ORDERED_COLUMN_IDS: 'ordered_column_ids',
    WIDTH_STATE: 'widthState',
    WRAP_TABLE_HEADER: 'wrapTableHeader',
    SHOW_TABLE_FOOTER: 'showTableFooter',
    SHOW_GRID_SUMMARY: 'showGridSummary',
    WRAP_COLUMN_TEXT: 'wrapColumnText',
    IS_HIDDEN: 'isHidden',
};
export function getDefaultPropsFromDefaultValues(defaultProps, props) {
    const defaultPropKeys = _.keys(defaultProps);
    const updatedProps = {
        ...defaultProps,
        ..._.pick(props, defaultPropKeys),
    };
    if (_.isEmpty(_.omit(updatedProps, '__typename'))) {
        return null;
    }
    return updatedProps;
}
export function getUpdatedFormatConfig(formatConfig) {
    return formatConfig
        ? {
            ...getDefaultPropsFromDefaultValues(DEFAULT_FORMAT_CONFIG, formatConfig),
            currencyFormatConfig: formatConfig.currencyFormatConfig
                ? {
                    ...getDefaultPropsFromDefaultValues(DEFAULT_CURRENCY_FORMAT_CONFIG, formatConfig.currencyFormatConfig),
                }
                : null,
            numberFormatConfig: formatConfig.numberFormatConfig
                ? {
                    ...getDefaultPropsFromDefaultValues(DEFAULT_NUMBER_FORMAT_CONFIG, formatConfig.numberFormatConfig),
                }
                : null,
            customFormatConfig: formatConfig.customFormatConfig
                ? {
                    ...getDefaultPropsFromDefaultValues(DEFAULT_CUSTOM_FORMAT_CONFIG, formatConfig.customFormatConfig),
                }
                : null,
            percentageFormatConfig: formatConfig.percentageFormatConfig
                ? {
                    ...getDefaultPropsFromDefaultValues(DEFAULT_PERCENTAGE_FORMAT_CONFIG, formatConfig.percentageFormatConfig),
                }
                : null,
        }
        : null;
}
//# sourceMappingURL=client-state-type.js.map